import "./ItemCategoryMaster.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import editIcon from "../../assets/icons/edit.png";
import BackIcon from "../../assets/icons/arrow.png";
import { useAuthStore } from "../../Services/authStore";
import { toastSuccess, toastError, toastWarning } from "../../Services/toaster";

// ─────────────────────────────────────────────────────────
//  ITEM CATEGORY MASTER COMPONENT
// ─────────────────────────────────────────────────────────
export default function ItemCategoryMaster() {
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // const [selectedItemCategory, setSelectedItemCategory] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [itemCategories, setItemCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    description: "",
    is_active: 1,
  });
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  const token = useAuthStore((state) => state.token);
  const API_URL = process.env.REACT_APP_API_URL;

  // ─── GET /item-category/list/all ───────────────────────────────────
  const getItemCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/item-category/list/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });

      // Handle response - map API fields to component fields
      const data = response.data?.data || response.data || [];
      const mappedCategories = data.map((item) => ({
        id: item.id || item.category_id,
        description: item.description,
        is_active: item.is_active === true || item.is_active === 1,
        created_at: item.created_at,
        updated_at: item.updated_at,
      }));

      setItemCategories(mappedCategories);
    } catch (err) {
      console.error("Failed to load item categories:", err.response?.data);
      toastError("Failed to load item categories");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    getItemCategories();
  }, [getItemCategories]);

  // ─── GET /item-category/{category_id} ───────────────────────────────────
  const getItemCategoryById = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${API_URL}/item-category/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });

        const data = response.data?.data || response.data;
        return {
          id: data.id || data.category_id,
          description: data.description,
          is_active: data.is_active === true || data.is_active === 1,
        };
      } catch (err) {
        console.error("Failed to fetch item category:", err.response?.data);
        throw err;
      }
    },
    [API_URL, token],
  );

  // ─── POST /item-category/add ───────────────────────────────────
  const createItemCategory = async (categoryData) => {
    try {
      const response = await axios.post(
        `${API_URL}/item-category/add`,
        {
          description: categoryData.description,
          is_active: categoryData.is_active === 1,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Create error:", err.response?.data);
      throw err;
    }
  };

  // ─── PUT /item-category/update/{category_id} ────────────────────────────
  const updateItemCategory = async (id, categoryData) => {
    try {
      const response = await axios.put(
        `${API_URL}/item-category/update/${id}`,
        {
          description: categoryData.description,
          is_active: categoryData.is_active === 1,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Update error:", err.response?.data);
      throw err;
    }
  };

  // ─── Open Edit ───────────────────────────────────────────────────
  const openEdit = async (itemCategory) => {
    setFormError("");
    setIsEditMode(true);
    // setSelectedItemCategory(itemCategory);
    setEditingId(itemCategory.id);
    setFormLoading(true);
    setShowForm(true);

    try {
      const categoryData = await getItemCategoryById(itemCategory.id);
      setFormData({
        description: categoryData.description || "",
        is_active: categoryData.is_active ? 1 : 0,
      });
    } catch (err) {
      const msg = "Failed to load item category details.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const openAdd = () => {
    setIsEditMode(false);
    // setSelectedItemCategory(null);
    setEditingId(null);
    setFormData({ description: "", is_active: 1 });
    setFormError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormData({ description: "", is_active: 1 });
    setFormError("");
    // setSelectedItemCategory(null);
    setEditingId(null);
    setIsEditMode(false);
  };

  const handleChange = (e) => {
    const value =
      e.target.type === "checkbox"
        ? e.target.checked
          ? 1
          : 0
        : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  // ─── Handle Submit (Create/Update) ───────────────────────────────────
  const handleSubmit = async () => {
    setFormError("");
    if (!formData.description.trim()) {
      const msg = "Item category description is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }
    setFormLoading(true);

    try {
      if (isEditMode) {
        // Update existing item category
        await updateItemCategory(editingId, formData);

        // Update local state
        setItemCategories((prev) =>
          prev.map((ic) =>
            ic.id === editingId
              ? {
                  ...ic,
                  description: formData.description,
                  is_active: formData.is_active === 1,
                }
              : ic,
          ),
        );

        toastSuccess("Item category updated successfully.");
      } else {
        // Create new item category
        await createItemCategory(formData);

        // Refresh the list
        await getItemCategories();

        toastSuccess("Item category created successfully.");
      }

      closeForm();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to save item category.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  // ─── Toggle Item Category Status ──────────────────────────────
  const toggleItemCategoryStatus = async (itemCategory) => {
    const newStatus = itemCategory.is_active ? 0 : 1;

    try {
      await updateItemCategory(itemCategory.id, {
        description: itemCategory.description,
        is_active: newStatus,
      });

      // Update local state
      setItemCategories((prev) =>
        prev.map((ic) =>
          ic.id === itemCategory.id
            ? { ...ic, is_active: newStatus === 1 }
            : ic,
        ),
      );

      toastSuccess(
        newStatus === 1
          ? "Item category activated successfully."
          : "Item category deactivated successfully.",
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to update item category status.";
      toastError(msg);
    }
  };

  // ── Filter item categories ──
  const filteredItemCategories = itemCategories.filter((itemCategory) =>
    itemCategory.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ════════════════════════════════════════════════════
  //  ADD / EDIT FORM
  // ════════════════════════════════════════════════════
  if (showForm) {
    return (
      <div className="icm-form-page">
        {/* Header with Back Arrow */}
        <div className="icm-form-header">
          <div className="icm-form-header-left">
            <button className="icm-back-btn" onClick={closeForm}>
              <img src={BackIcon} alt="Back" className="icm-back-icon" />
            </button>
            <div>
              <h2 className="icm-form-title">
                {isEditMode ? "Edit Item Category" : "Add Item Category"}
              </h2>
              <p className="icm-form-subtitle">
                {isEditMode
                  ? "Update item category description and status"
                  : "Create a new item category classification"}
              </p>
            </div>
          </div>
        </div>

        <div className="icm-form-card">
          {formLoading && (
            <p style={{ color: "#9F8E87", fontSize: 13, marginBottom: 14 }}>
              {isEditMode ? "Loading..." : "Saving..."}
            </p>
          )}
          {formError && (
            <p
              style={{
                color: "#b94040",
                fontSize: 13,
                marginBottom: 14,
                whiteSpace: "pre-line",
              }}
            >
              {formError}
            </p>
          )}

          <div className="icm-form-grid">
            {/* Description */}
            <div className="icm-form-group">
              <label>Item Category Description *</label>
              <input
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter item category description"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
            </div>

            {/* Status — only show in edit mode */}
            {isEditMode && (
              <div className="icm-form-group">
                <label>Status</label>
                <div className="icm-status-toggle">
                  <label className="icm-switch">
                    <input
                      type="checkbox"
                      checked={formData.is_active === 1}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_active: e.target.checked ? 1 : 0,
                        })
                      }
                    />
                    <span className="icm-slider"></span>
                  </label>
                  <span className="icm-status-label">
                    {formData.is_active === 1 ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="icm-form-footer">
            <button className="icm-btn icm-btn-secondary" onClick={closeForm}>
              Cancel
            </button>
            <button
              className="icm-btn icm-btn-primary"
              onClick={handleSubmit}
              disabled={formLoading}
            >
              {formLoading
                ? "Saving..."
                : isEditMode
                  ? "Update Item Category"
                  : "Save Item Category"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  //  ITEM CATEGORY LIST
  // ════════════════════════════════════════════════════
  return (
    <div className="icm-list-page">
      {/* ─── Top Bar ─── */}
      <div className="icm-page-header">
        <div>
          <h1 className="icm-page-title">Item Category Master</h1>
          <p className="icm-page-subtitle">
            Manage item categories and classifications
          </p>
        </div>
        <div className="icm-page-actions">
          <div className="icm-search-box">
            <span className="icm-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search item categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="icm-btn icm-btn-primary" onClick={openAdd}>
            + Add Item Category
          </button>
        </div>
      </div>

      {/* ─── Table ─── */}
      <div className="icm-table-card">
        <div className="icm-table-responsive">
          <table className="icm-table">
            <thead>
              <tr>
                <th className="icm-col-id">#</th>
                <th className="icm-col-description">Description</th>
                <th className="icm-col-active">Status</th>
                <th className="icm-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="icm-empty-row">
                    Loading item categories...
                  </td>
                </tr>
              ) : filteredItemCategories.length === 0 ? (
                <tr>
                  <td colSpan="4">
                    <div className="icm-empty-state">
                      <div className="icm-empty-icon">📦</div>
                      <div className="icm-empty-title">
                        No Item Categories Found
                      </div>
                      <div className="icm-empty-desc">
                        {searchTerm
                          ? `No item categories match "${searchTerm}"`
                          : "Start by adding your first item category"}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredItemCategories.map((itemCategory, index) => (
                  <tr key={itemCategory.id}>
                    <td className="icm-col-id">{index + 1}</td>
                    <td className="icm-col-description">
                      {itemCategory.description}
                    </td>
                    <td className="icm-col-active">
                      <label className="icm-switch">
                        <input
                          type="checkbox"
                          checked={itemCategory.is_active}
                          onChange={() =>
                            toggleItemCategoryStatus(itemCategory)
                          }
                        />
                        <span className="icm-slider"></span>
                      </label>
                    </td>
                    <td className="icm-col-actions">
                      <div className="icm-action-group">
                        <button
                          className="icm-action-btn icm-action-btn-edit"
                          onClick={() => openEdit(itemCategory)}
                          title="Edit"
                        >
                          <img
                            src={editIcon}
                            alt="Edit"
                            className="icm-action-icon"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
